import React, { useEffect, useState } from "react";
import {
  Save,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Image as ImageIcon,
  Clock3,
  ExternalLink,
  Settings,
} from "lucide-react";
import { ADMIN } from "@/lib/testIds";
import {
  useRestaurant,
  setRestaurant,
  resetRestaurant,
} from "@/lib/brandingStore";

const TextField = ({ label, name, value, onChange, placeholder, type = "text", hint }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-street">
      {label}
    </span>
    <input
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={`admin-restaurant-${name}`}
      className="mt-1 w-full rounded-md border border-[#262626] bg-[#0A0A0A] py-2 px-3 text-sm text-white placeholder:text-white/30 focus:border-[#FF9500] focus:outline-none transition-colors"
    />
    {hint && <div className="mt-1 text-[11px] text-white/45 leading-snug">{hint}</div>}
  </label>
);

const AdminParametres = () => {
  const current = useRestaurant();
  const [form, setForm] = useState(current);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(current);
  }, [current]);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setSaved(false);
  };

  const onHourChange = (idx, range) => {
    const next = [...form.hours];
    next[idx] = { ...next[idx], range };
    setForm({ ...form, hours: next });
    setSaved(false);
  };

  const save = (e) => {
    e.preventDefault();
    setRestaurant(form);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  const reset = () => {
    if (!window.confirm("Restaurer les paramètres par défaut ?")) return;
    resetRestaurant();
  };

  return (
    <div data-testid={ADMIN.parametres} className="space-y-8">
      <header>
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#FF9500] font-street inline-flex items-center gap-2">
          <Settings size={12} /> Informations restaurant
        </div>
        <h1 className="font-street mt-2 text-3xl tracking-wide">Paramètres restaurant</h1>
        <p className="mt-2 text-sm text-white/55 leading-snug max-w-2xl">
          Ces informations alimentent la page Contact, le footer, le bloc
          "Sur place", les boutons d'appel, et les balises SEO. Modifiez ici —
          les changements s'appliquent immédiatement.
        </p>
      </header>

      <form onSubmit={save} className="space-y-6">
        {/* Identité */}
        <section className="rounded-2xl border border-[#262626] bg-[#0F0F0F] p-5 sm:p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF9500] font-street mb-3">
            Identité
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Nom de l'établissement" name="name" value={form.name} onChange={onChange} />
            <TextField label="Slogan / accroche" name="slogan" value={form.slogan} onChange={onChange} />
          </div>
        </section>

        {/* Adresse + contact */}
        <section className="rounded-2xl border border-[#262626] bg-[#0F0F0F] p-5 sm:p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF9500] font-street mb-3">
            Adresse et coordonnées
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Adresse (rue)" name="address_street" value={form.address_street} onChange={onChange} />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Code postal" name="address_zip" value={form.address_zip} onChange={onChange} />
              <TextField label="Ville" name="address_city" value={form.address_city} onChange={onChange} />
            </div>
            <TextField
              label="Téléphone (affiché)"
              name="phone_display"
              value={form.phone_display}
              onChange={onChange}
              placeholder="01 39 94 19 19"
            />
            <TextField
              label="Téléphone (lien tel:)"
              name="phone_tel"
              value={form.phone_tel}
              onChange={onChange}
              placeholder="tel:+33139941919"
              hint="Format international, sans espace. Exemple : tel:+33139941919"
            />
            <TextField label="Email" name="email" value={form.email} onChange={onChange} type="email" />
            <TextField label="Nom de domaine" name="domain" value={form.domain} onChange={onChange} />
            <TextField
              label="Lien Google Maps"
              name="maps_url"
              value={form.maps_url}
              onChange={onChange}
              placeholder="https://www.google.com/maps/search/?api=1&query=…"
              hint="Utilisé par le bouton « Itinéraire »."
            />
          </div>
        </section>

        {/* Horaires */}
        <section className="rounded-2xl border border-[#262626] bg-[#0F0F0F] p-5 sm:p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF9500] font-street mb-3 inline-flex items-center gap-2">
            <Clock3 size={11} /> Horaires d'ouverture
          </div>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {(form.hours || []).map((h, i) => (
              <label
                key={h.day}
                className="flex items-center gap-3 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 py-2.5"
              >
                <span className="w-20 shrink-0 text-[11px] uppercase tracking-[0.22em] text-white/55 font-street">
                  {h.day}
                </span>
                <input
                  type="text"
                  value={h.range || ""}
                  onChange={(e) => onHourChange(i, e.target.value)}
                  placeholder="11h00 – 23h00"
                  data-testid={`admin-restaurant-hours-${h.day.toLowerCase()}`}
                  className="flex-1 bg-transparent border-0 px-0 py-0 text-sm text-white placeholder:text-white/30 focus:outline-none"
                />
              </label>
            ))}
          </div>
        </section>

        {/* Photo */}
        <section className="rounded-2xl border border-[#262626] bg-[#0F0F0F] p-5 sm:p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF9500] font-street mb-3 inline-flex items-center gap-2">
            <ImageIcon size={11} /> Photo principale du restaurant
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <TextField
                label="URL de la photo"
                name="photo_url"
                value={form.photo_url}
                onChange={onChange}
                placeholder="https://…/restaurant.jpg"
                hint="Si vide, le visuel « 11 » reste affiché en fallback sur la page Contact."
              />
            </div>
            <div className="rounded-xl border border-[#262626] bg-[#0A0A0A] h-48 overflow-hidden flex items-center justify-center">
              {form.photo_url ? (
                <img
                  src={form.photo_url}
                  alt="Aperçu restaurant"
                  data-testid="admin-restaurant-photo-preview"
                  className="h-full w-full object-cover"
                  onError={(e) => (e.target.style.opacity = "0.3")}
                />
              ) : (
                <div className="font-display text-7xl text-[#FF9500]/30">11</div>
              )}
            </div>
          </div>
        </section>

        {saved && (
          <div
            role="status"
            data-testid="admin-restaurant-saved"
            className="inline-flex items-center gap-2 rounded-md border border-[#22C55E]/40 bg-[#22C55E]/10 px-3 py-2 text-[12px] text-[#86EFAC]"
          >
            <CheckCircle2 size={13} /> Paramètres enregistrés.
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="submit"
            data-testid="admin-restaurant-save"
            className="font-street inline-flex items-center gap-2 rounded-md bg-[#FF9500] px-5 py-2.5 text-sm tracking-wider text-black shadow-[0_4px_0_#7a4900] hover:-translate-y-0.5 transition-all"
          >
            <Save size={14} /> Enregistrer
          </button>
          <button
            type="button"
            onClick={reset}
            data-testid="admin-restaurant-reset"
            className="font-street inline-flex items-center gap-2 rounded-md border border-[#262626] px-4 py-2.5 text-sm tracking-wider text-white/70 hover:text-white hover:border-white/30 transition-colors"
          >
            <RotateCcw size={13} /> Réinitialiser
          </button>
          <a
            href="/contact"
            target="_blank"
            rel="noreferrer"
            className="font-street inline-flex items-center gap-1.5 rounded-md border border-[#262626] px-4 py-2.5 text-sm tracking-wider text-white/70 hover:text-white"
          >
            Voir la page Contact <ExternalLink size={12} />
          </a>
        </div>

        <div className="text-[11px] text-white/40 leading-snug">
          <AlertCircle size={11} className="inline-block mr-1 -mt-0.5" />
          Données stockées localement (Phase 0). Pas de synchronisation entre
          appareils. Migration MongoDB prévue en Phase 1.
        </div>
      </form>
    </div>
  );
};

export default AdminParametres;
